<template>
  <div ref="navigation" class="navigation-shell">
    <header
      class="nav"
      :class="{ 'nav--open': activeMenu }"
      :style="{ backgroundColor: navBgcolor }"
    >
      <div class="nav-container">
        <router-link to="/index" class="logo" aria-label="返回首页">
          <img :src="logoDark" alt="禾祺科技" />
        </router-link>

        <nav class="desktop-navigation" aria-label="主导航">
          <ul class="desktop-menu">
            <li
              v-for="item in menuItems"
              :key="item.key"
              class="menu-item"
              :class="{ 'menu-item--active': isMenuActive(item) }"
              @mouseenter="item.children ? openMenu(item.key) : closeMenu()"
              @mouseleave="scheduleCloseMenu"
            >
              <button
                v-if="item.children"
                :id="`menu-trigger-${item.key}`"
                class="menu-trigger"
                type="button"
                aria-haspopup="true"
                :aria-expanded="activeMenu === item.key ? 'true' : 'false'"
                :aria-controls="`mega-menu-${item.key}`"
                @click="toggleMenu(item.key)"
                @focus="openMenu(item.key)"
              >
                {{ item.label }}
                <span class="menu-chevron" aria-hidden="true"></span>
              </button>
              <router-link
                v-else
                class="menu-link"
                :to="item.to"
                @focus.native="closeMenu"
              >
                {{ item.label }}
              </router-link>
            </li>
          </ul>
        </nav>

        <div class="nav-actions">
          <a class="hotline" href="tel:4008803557">
            <img src="../assets/images/contact-tel.png" alt="咨询热线" />
            <span>
              <strong>400-880-3557</strong>
              <strong>15735645888</strong>
            </span>
          </a>
          <el-button class="trial-button" type="primary" round @click="openTrialDialog">
            免费体验
          </el-button>
        </div>

        <button
          class="mobile-toggle"
          type="button"
          aria-label="打开导航菜单"
          aria-controls="mobile-navigation"
          :aria-expanded="navShow ? 'true' : 'false'"
          @click="toggleMobileMenu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>

    <transition name="mega-menu">
      <section
        v-if="activeMenuItem"
        :id="`mega-menu-${activeMenuItem.key}`"
        class="mega-menu"
        :aria-labelledby="`menu-trigger-${activeMenuItem.key}`"
        @mouseenter="cancelCloseMenu"
        @mouseleave="scheduleCloseMenu"
      >
        <div class="mega-menu-inner">
          <div class="mega-intro">
            <span class="mega-eyebrow">禾祺科技 / {{ activeMenuItem.label }}</span>
            <h2>{{ activeMenuItem.title }}</h2>
            <p>{{ activeMenuItem.description }}</p>
            <router-link v-if="activeMenuItem.overview" :to="activeMenuItem.overview" class="overview-link" @click.native="closeMenu">
              查看{{ activeMenuItem.label }}<span aria-hidden="true">→</span>
            </router-link>
          </div>
          <div class="mega-links">
            <router-link
              v-for="link in activeMenuItem.children"
              :key="link.label"
              :to="link.to"
              class="mega-link"
              @click.native="closeMenu"
            >
              <span>
                <strong>{{ link.label }}</strong>
                <small>{{ link.description }}</small>
              </span>
            </router-link>
          </div>
          <div v-if="activeMenuItem.image" class="mega-feature">
            <img :src="activeMenuItem.image" :alt="activeMenuItem.imageAlt" />
            <span>{{ activeMenuItem.feature }}</span>
          </div>
        </div>
      </section>
    </transition>

    <transition name="mobile-menu">
      <div v-if="navShow" id="mobile-navigation" class="mobile-navigation">
        <div v-for="item in menuItems" :key="item.key" class="mobile-item">
          <button
            v-if="item.children"
            class="mobile-trigger"
            type="button"
            :aria-expanded="expandedMobileMenu === item.key ? 'true' : 'false'"
            @click="toggleMobileSection(item.key)"
          >
            {{ item.label }}
            <span class="mobile-chevron" :class="{ 'mobile-chevron--open': expandedMobileMenu === item.key }" aria-hidden="true"></span>
          </button>
          <router-link v-else class="mobile-link" :to="item.to" @click.native="closeMobileMenu">
            {{ item.label }}
          </router-link>
          <div v-if="item.children && expandedMobileMenu === item.key" class="mobile-submenu">
            <router-link v-for="link in item.children" :key="link.label" :to="link.to" @click.native="closeMobileMenu">
              {{ link.label }}
            </router-link>
          </div>
        </div>
        <div class="mobile-contact">
          <a href="tel:4008803557">咨询热线：400-880-3557</a>
          <el-button class="trial-button" type="primary" round @click="openTrialDialog(); closeMobileMenu()">
            免费体验
          </el-button>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>

export default {
  name: 'NavAndSide',
  inject: ['openTrialDialog'],
  data () {
    return {
      activeMenu: null,
      expandedMobileMenu: null,
      closeTimer: null,
      navShow: false,
      navBgcolor: '#fff',
      logoDark: require('@/assets/images/logo5.png'),
      menuItems: [
        {
          key: 'home',
          label: '首页',
          to: '/index'
        },
        {
          key: 'solutions',
          label: '解决方案',
          title: '以档案数字化\n驱动组织持续发展',
          description: '面向政府、企业与高校，提供覆盖档案全生命周期的数字化解决方案。',
          overview: { path: '/digitalArchive', query: { id: 'dangan' } },
          image: require('../assets/images/nav/pexels-photo-5439148.png'),
          imageAlt: '数字化档案服务',
          feature: '让档案管理更高效、更安全',
          children: [
            { label: '数字档案管理', description: '构建一体化数字档案体系', to: { path: '/digitalArchive', query: { id: 'dangan' } } },
            { label: '集团企业档案管理', description: '统一管理多组织档案资产', to: { path: '/digitalArchive', query: { id: 'jituan' } } },
            { label: '高校档案管理', description: '助力高校档案智慧升级', to: { path: '/digitalArchive', query: { id: 'gaoxiao' } } },
            { label: '档案馆三网建设', description: '打造安全可靠的档案馆网络', to: { path: '/digitalArchive', query: { id: 'danganguan' } } }
          ]
        },
        {
          key: 'products',
          label: '产品中心',
          title: '让档案管理回归简单',
          description: '以成熟的软件产品与智能能力\n帮助组织提升档案管理质量与工作效率。',
          overview: '/products',
          image: require('../assets/images/nav/ry.jpeg'),
          imageAlt: '档案管理产品',
          feature: '专业产品，适配多种业务场景',
          children: [
            { label: '档案管理平台', description: '档案业务一体化管理平台', to: '/product/manage' },
            { label: '档案四性检测', description: '保障电子档案真实完整可用', to: '/product/use' },
            { label: '声像档案管理', description: '集中管理多媒体档案资源', to: '/product/identification' },
            { label: '档案智能著录', description: '智能识别与高效著录档案', to: '/product/intelligence' },
            { label: '智能开放鉴定系统', description: '智能识别、规则配置与协同审核', to: '/product/open-identification' },
            { label: '知识管理系统', description: '统一沉淀、检索与复用组织知识', to: '/product/knowledge' }
          ]
        },
        {
          key: 'cooperation',
          label: '合作',
          to: '/cooperation'
        },
        {
          key: 'instance',
          label: '案例',
          to: '/instance'
        },
        {
          key: 'news',
          label: '新闻公告',
          to: '/news'
        },
        {
          key: 'consultation',
          label: '行业资讯',
          to: '/consultation'
        },
        {
          key: 'company',
          label: '关于我们',
          title: '专注档案服务\n创造长期价值',
          description: '禾祺科技深耕档案行业，为客户提供值得信赖的专业服务与数字化能力。',
          overview: '/introduce',
          image: require('../assets/images/nav/hz.jpeg'),
          imageAlt: '禾祺科技服务团队',
          feature: '与客户共同探索档案新价值',
          children: [
            { label: '企业介绍', description: '了解禾祺科技的发展与理念', to: '/introduce' },
            { label: '联系我们', description: '与我们取得联系', to: '/contact' },
            { label: '加入我们', description: '加入专业团队，共创美好未来', to: '/recruitList' },
            { label: '企业资质', description: '查看企业专业资质与认证', to: '/aptitudeShow' }
          ]
        }
      ]
    }
  },
  computed: {
    activeMenuItem () {
      return this.menuItems.find(item => item.key === this.activeMenu) || null
    }
  },
  methods: {
    isMenuActive (item) {
      const path = this.$route.path
      if (item.key === 'home') return path === '/index'
      if (item.key === 'products') return path.indexOf('/product') === 0 || path === '/products'
      if (item.key === 'solutions') return path === '/solution' || path === '/digitalArchive'
      if (item.key === 'company') return ['/introduce', '/contact', '/recruitList', '/aptitudeShow'].indexOf(path) !== -1
      return path === item.to
    },
    openMenu (key) {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
      this.activeMenu = key
    },
    toggleMenu (key) {
      if (this.activeMenu === key) {
        this.closeMenu()
      } else {
        this.openMenu(key)
      }
    },
    scheduleCloseMenu () {
      if (this.closeTimer) clearTimeout(this.closeTimer)
      this.closeTimer = setTimeout(() => {
        this.activeMenu = null
        this.closeTimer = null
      }, 140)
    },
    cancelCloseMenu () {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },
    closeMenu () {
      this.cancelCloseMenu()
      this.activeMenu = null
    },
    toggleMobileMenu () {
      this.navShow = !this.navShow
      if (!this.navShow) this.expandedMobileMenu = null
    },
    toggleMobileSection (key) {
      this.expandedMobileMenu = this.expandedMobileMenu === key ? null : key
    },
    closeMobileMenu () {
      this.navShow = false
      this.expandedMobileMenu = null
    },
    handleOutsideClick (event) {
      if (this.$refs.navigation && !this.$refs.navigation.contains(event.target)) {
        this.closeMenu()
        this.closeMobileMenu()
      }
    },
    handleEscape (event) {
      if (event.key === 'Escape') {
        this.closeMenu()
        this.closeMobileMenu()
      }
    },
    handleScroll () {
      this.navBgcolor = '#fff'
    }
  },
  mounted () {
    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleEscape)
    window.addEventListener('scroll', this.handleScroll)
  },
  destroyed () {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleEscape)
    window.removeEventListener('scroll', this.handleScroll)
    if (this.closeTimer) clearTimeout(this.closeTimer)
  },
  watch: {
    $route: {
      handler () {
        this.closeMenu()
        this.closeMobileMenu()
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.navigation-shell {
  position: relative;
  z-index: 9999;
}

.nav {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
  width: 100%;
  height: 72px;
  border-bottom: 1px solid #edf1f6;
  box-shadow: 0 5px 22px rgba(23, 49, 81, 0.06);
  transition: box-shadow 0.25s ease, background-color 0.25s ease;
}

.nav--open {
  box-shadow: 0 5px 24px rgba(23, 49, 81, 0.1);
}

.nav-container {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  width: min(1320px, calc(100% - 64px));
  height: 72px;
  margin: 0 auto;
}

.logo {
  display: flex;
  align-items: center;
  justify-self: start;
  height: 100%;
}

.logo img {
  display: block;
  width: 166px;
  height: auto;
}

.desktop-navigation {
  width: max-content;
  height: 100%;
  justify-self: center;
}

.desktop-menu {
  display: flex;
  align-items: stretch;
  height: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
}

.menu-item {
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 3px;
}

.menu-link,
.menu-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 100%;
  padding: 0 15px;
  border: 0;
  background: transparent;
  color: #25364b;
  font-size: 17px;
  font-weight: 500;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
}

.menu-link::after,
.menu-trigger::after {
  position: absolute;
  right: 15px;
  bottom: 0;
  left: 15px;
  height: 3px;
  background: #1f5dea;
  content: '';
  opacity: 0;
  transform: scaleX(0.3);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.menu-link:hover,
.menu-link:focus,
.menu-trigger:hover,
.menu-trigger:focus {
  outline: none;
}

.menu-link:focus-visible,
.menu-trigger:focus-visible {
  border-radius: 4px;
  box-shadow: 0 0 0 3px rgba(31, 93, 234, 0.16);
}

.menu-item--active .menu-link,
.menu-item--active .menu-trigger {
  font-weight: 700;
}

.menu-chevron {
  width: 7px;
  height: 7px;
  margin: -4px 0 0 9px;
  border-right: 1px solid currentColor;
  border-bottom: 1px solid currentColor;
  transform: rotate(45deg);
  transition: transform 0.2s ease;
}

.menu-trigger[aria-expanded='true'] .menu-chevron {
  margin-top: 4px;
  transform: rotate(225deg);
}

.nav-actions {
  display: flex;
  align-items: center;
  justify-self: end;
  gap: 18px;
}

.hotline {
  display: flex;
  align-items: center;
  color: #607087;
  text-decoration: none;
  white-space: nowrap;
}

.hotline img {
  width: 25px;
  margin-right: 8px;
}

.hotline span {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.hotline strong {
  color: #263b57;
  font-size: 14px;
  font-weight: 600;
}

.hotline small {
  margin-top: 3px;
  color: #9aa8b8;
  font-size: 10px;
}

.trial-button {
  min-width: 104px;
  height: 38px;
  border: 0 !important;
  background: #1f5dea !important;
  box-shadow: 0 8px 16px rgba(31, 93, 234, 0.18);
  font-size: 13px;
}

.mega-menu {
  position: fixed;
  top: 72px;
  left: 0;
  z-index: 9;
  width: 100%;
  min-height: 286px;
  border-top: 1px solid #edf1f6;
  background: #fff;
  box-shadow: 0 18px 35px rgba(23, 49, 81, 0.13);
}

.mega-menu-inner {
  display: grid;
  grid-template-columns: minmax(220px, 0.85fr) minmax(420px, 1.55fr) minmax(190px, 0.7fr);
  gap: 42px;
  width: min(1120px, calc(100% - 64px));
  min-height: 286px;
  margin: 0 auto;
  padding: 38px 0 34px;
}

.mega-intro {
  padding-right: 25px;
  border-right: 1px solid #edf1f6;
}

.mega-eyebrow {
  font-size: 14px;
  letter-spacing: 0.08em;
}

.mega-intro h2 {
  margin: 18px 0 12px;
  white-space: pre-line;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.45;
}

.mega-intro p {
  margin: 0;
  color: #7c8a9a;
  font-size: 13px;
  line-height: 1.9;
}

.overview-link {
  display: inline-flex;
  align-items: center;
  margin-top: 23px;
  color: #1f5dea;
  font-size: 13px;
  text-decoration: none;
}

.overview-link span {
  margin-left: 10px;
  font-size: 17px;
  transition: transform 0.2s ease;
}

.overview-link:hover span {
  transform: translateX(4px);
}

.mega-links {
  display: grid;
  grid-template-columns: repeat(2, minmax(180px, 1fr));
  align-content: center;
  gap: 12px 18px;
}

.mega-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 76px;
  padding: 14px 16px;
  border: 1px solid #edf1f6;
  color: #263b57;
  text-decoration: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.mega-link:hover,
.mega-link:focus {
  border-color: rgba(31, 93, 234, 0.35);
  box-shadow: 0 8px 20px rgba(31, 93, 234, 0.09);
  outline: none;
  transform: translateY(-2px);
}

.mega-link strong,
.mega-link small {
  display: block;
}

.mega-link strong {
  font-size: 14px;
  font-weight: 500;
}

.mega-link small {
  margin-top: 7px;
  color: #94a0ae;
  font-size: 11px;
}

.link-arrow {
  color: #1f5dea;
  font-size: 18px;
}

.mega-feature {
  position: relative;
  min-height: 214px;
  overflow: hidden;
  background: #20344e;
}

.mega-feature img {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 214px;
  object-fit: cover;
  opacity: 0.75;
}

.mega-feature::after {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 55%;
  background: linear-gradient(transparent, rgba(20, 38, 61, 0.85));
  content: '';
}

.mega-feature span {
  position: absolute;
  right: 17px;
  bottom: 17px;
  left: 17px;
  z-index: 1;
  color: #fff;
  font-size: 13px;
  line-height: 1.6;
}
.mobile-toggle,
.mobile-navigation {
  display: none;
}

.mega-menu-enter-active,
.mega-menu-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.mega-menu-enter,
.mega-menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 1200px) {
  .nav-container {
    width: min(1080px, calc(100% - 64px));
  }

  .logo {
    flex-basis: 150px;
  }

  .logo img {
    width: 145px;
  }

  .menu-link,
  .menu-trigger {
    padding: 0 10px;
    font-size: 13px;
  }

  .menu-link::after,
  .menu-trigger::after {
    right: 10px;
    left: 10px;
  }

  .hotline small {
    display: none;
  }

  .nav-actions {
    gap: 10px;
  }

  .mega-menu-inner {
    gap: 28px;
  }
}

@media (max-width: 1120px) and (min-width: 993px) {
  .hotline {
    display: none;
  }

  .nav-actions {
    gap: 0;
  }
}

@media (max-width: 992px) {
  .nav {
    height: 64px;
  }

  .nav-container {
    width: calc(100% - 32px);
    height: 64px;
  }

  .logo {
    flex-basis: auto;
  }

  .logo img {
    width: 148px;
  }

  .desktop-navigation,
  .nav-actions,
  .mega-menu {
    display: none;
  }

  .mobile-toggle {
    grid-column: 3;
    justify-self: end;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    width: 42px;
    height: 42px;
    margin-left: auto;
    padding: 8px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
  }

  .mobile-toggle:focus-visible {
    box-shadow: 0 0 0 3px rgba(31, 93, 234, 0.16);
    outline: none;
  }

  .mobile-toggle span {
    display: block;
    width: 25px;
    height: 2px;
    margin: 0 auto;
    background: #25364b;
  }

  .mobile-navigation {
    position: fixed;
    top: 64px;
    right: 0;
    left: 0;
    z-index: 8;
    display: block;
    max-height: calc(100vh - 64px);
    overflow-y: auto;
    border-top: 1px solid #edf1f6;
    background: #fff;
    box-shadow: 0 18px 35px rgba(23, 49, 81, 0.13);
  }

  .mobile-item {
    border-bottom: 1px solid #edf1f6;
  }

  .mobile-link,
  .mobile-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 52px;
    padding: 0 28px;
    border: 0;
    background: #fff;
    color: #25364b;
    font-size: 15px;
    text-align: left;
    text-decoration: none;
  }

  .mobile-trigger {
    cursor: pointer;
  }

  .mobile-link:focus,
  .mobile-trigger:focus {
    background: #f5f8fd;
    outline: none;
  }

  .mobile-chevron {
    width: 8px;
    height: 8px;
    border-right: 1px solid #7890aa;
    border-bottom: 1px solid #7890aa;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
  }

  .mobile-chevron--open {
    transform: rotate(225deg);
  }

  .mobile-submenu {
    padding: 5px 0 8px;
    background: #f7f9fc;
  }

  .mobile-submenu a {
    display: block;
    padding: 11px 48px;
    color: #66778d;
    font-size: 13px;
    text-decoration: none;
  }

  .mobile-submenu a:hover,
  .mobile-submenu a:focus {
    color: #1f5dea;
    outline: none;
  }

  .mobile-contact {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    padding: 20px 28px 25px;
  }

  .mobile-contact a {
    color: #61748b;
    font-size: 13px;
    text-decoration: none;
  }

  .mobile-contact .trial-button {
    flex: 0 0 auto;
  }

  .mobile-menu-enter-active,
  .mobile-menu-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  .mobile-menu-enter,
  .mobile-menu-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .nav,
  .menu-link::after,
  .menu-trigger::after,
  .menu-chevron,
  .mega-menu-enter-active,
  .mega-menu-leave-active,
  .mobile-menu-enter-active,
  .mobile-menu-leave-active {
    transition: none;
  }
}
</style>
